import agwaSearchDialogStyles from './agwa-search-dialog.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { SearchMetaDto, SearchResultRow } from '../../api/agwa-api-model';
import { getSearchApi } from '../../api/search/search-api';
import { getCachedMeta, setCachedMeta } from './search-meta-cache';
import { focusFirstFieldIfDialogStillFocused } from '../../utility/dialog-initial-focus';

import '../state-badge/agwa-state-badge';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';

const SLICE_LENGTH = 20;
const FREE_TEXT_DEBOUNCE_MS = 250;

export interface SearchDialogSelection {
  id: string;
  displayValue: string;
  row: SearchResultRow;
}

/**
 * Generic, host-controlled entity search dialog. Backed by the server-side
 * search handler registry (GET /api/search/meta, POST /api/search). Owns meta
 * loading, the result table, free-text search, paging, and keyboard nav — but
 * NOT selection persistence: it emits the chosen row and lets the host decide
 * what to do (cache it, link it, etc.).
 *
 * This is the reusable half extracted from `agwa-search-picker`. The picker
 * mounts it for its combo-input flow; other hosts (e.g. a "Add Product" button)
 * mount it directly.
 *
 * The `context` prop is forwarded verbatim to the search request — it is an
 * engine-interpreted dictionary of extra scoping values (e.g. the product
 * engine reads `ownerId` / `status`). The dialog itself does not interpret it.
 *
 * Open/close is controlled by the host via the `open` property. The dialog
 * never closes itself on selection — it emits `select` and the host closes.
 *
 * Events:
 *  - select     — detail: SearchDialogSelection (the picked row)
 *  - dialog-hide — fired when the user cancels/closes (host should set open=false)
 */
@customElement('agwa-search-dialog')
export class AgwaSearchDialog extends LitElement {
  /** The server-side handler tag (e.g. "product"). */
  @property({ type: String }) tag = '';

  /** Host-controlled visibility. */
  @property({ type: Boolean }) open = false;

  /** Dialog title hint (rendered as "Select {label}"). */
  @property({ type: String }) label = '';

  /** Optional seed text applied to the search box when the dialog opens. */
  @property({ type: String }) initialText = '';

  /**
   * Engine-interpreted scoping dictionary, forwarded verbatim to the search
   * request. Keys are defined by the target handler (e.g. product engine:
   * `ownerId`, `status`); unknown keys are ignored server-side.
   */
  @property({ attribute: false }) context?: Record<string, string>;

  @state() private _meta: SearchMetaDto | null = null;
  @state() private _metaLoading = false;
  @state() private _metaError: string | null = null;
  @state() private _rows: SearchResultRow[] = [];
  @state() private _hasMore = false;
  @state() private _loading = false;
  @state() private _error: string | null = null;
  @state() private _freeText = '';
  @state() private _highlightedIndex = -1;

  private _sliceStart = 0;
  private _debounceHandle: ReturnType<typeof setTimeout> | undefined;

  private static _instanceCounter = 0;
  private readonly _instanceId = `agwa-search-dialog-${++AgwaSearchDialog._instanceCounter}`;
  static styles = unsafeCSS(agwaSearchDialogStyles);

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._debounceHandle !== undefined) {
      clearTimeout(this._debounceHandle);
    }
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('open')) {
      if (this.open) {
        void this._onOpened();
      } else if (this._debounceHandle !== undefined) {
        clearTimeout(this._debounceHandle);
        this._debounceHandle = undefined;
      }
    }
  }

  private async _onOpened() {
    this._freeText = this.initialText ?? '';
    this._rows = [];
    this._hasMore = false;
    this._error = null;
    this._sliceStart = 0;
    this._highlightedIndex = -1;
    await this._ensureMeta();
    if (this._meta) {
      void this._focusSearchOnceRendered();
      await this._loadRows(false);
    }
  }

  /**
   * The search box renders only once the search settings are here. On a first open they come from
   * the server, after the app-wide dialog focus rule has finished waiting, so the box would open
   * without the cursor (FORM-FOCUS rule 4, the server-loaded exception). Asked once, when the box
   * has rendered; a cursor already placed (autofocus on a cached open, or the person's own click)
   * is left where it is.
   */
  private async _focusSearchOnceRendered() {
    await this.updateComplete;
    const dialog = this.shadowRoot?.querySelector('sl-dialog');
    if (dialog) await focusFirstFieldIfDialogStillFocused(dialog);
  }

  private _requestClose() {
    this.dispatchEvent(
      new CustomEvent('dialog-hide', { bubbles: true, composed: true })
    );
  }

  private _onRowClick(row: SearchResultRow) {
    if (this._debounceHandle !== undefined) {
      clearTimeout(this._debounceHandle);
      this._debounceHandle = undefined;
    }
    this.dispatchEvent(
      new CustomEvent<SearchDialogSelection>('select', {
        detail: { id: row.id, displayValue: row.displayValue, row },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async _ensureMeta() {
    const cached = getCachedMeta(this.tag);
    if (cached) {
      this._meta = cached;
      return;
    }
    this._metaLoading = true;
    this._metaError = null;
    try {
      const result = await getSearchApi().getMeta(this.tag);
      if (result.success) {
        setCachedMeta(this.tag, result.data);
        this._meta = result.data;
      } else {
        this._metaError =
          result.error ?? 'Failed to load search configuration.';
      }
    } catch {
      this._metaError = 'Failed to load search configuration.';
    } finally {
      this._metaLoading = false;
    }
  }

  private async _loadRows(append: boolean) {
    if (!append) this._sliceStart = 0;
    this._loading = true;
    this._error = null;
    try {
      const result = await getSearchApi().executeSearch({
        tag: this.tag,
        sliceStart: this._sliceStart,
        sliceLength: SLICE_LENGTH,
        freeText: this._freeText || undefined,
        requiresCount: !append && this._sliceStart === 0,
        context: this.context,
      });
      if (result.success) {
        const page = result.data;
        this._rows = append ? [...this._rows, ...page.items] : page.items;
        this._hasMore = page.hasMore;
        this._sliceStart = this._rows.length;
        if (!append) this._highlightedIndex = this._rows.length > 0 ? 0 : -1;
      } else {
        this._error = result.error ?? 'Search failed.';
      }
    } catch {
      this._error = 'Search failed.';
    } finally {
      this._loading = false;
    }
  }

  private _onFreeTextInput(e: CustomEvent) {
    const target = e.currentTarget as HTMLInputElement;
    this._freeText = target.value ?? '';
    if (this._debounceHandle !== undefined) clearTimeout(this._debounceHandle);
    this._debounceHandle = setTimeout(() => {
      void this._loadRows(false);
    }, FREE_TEXT_DEBOUNCE_MS);
  }

  private _onDialogSearchKeyDown(e: KeyboardEvent) {
    // Escape closes the dialog (sl-dialog's own handler, which ignores defaultPrevented). The box is
    // type="search", so an uncancelled Escape would also clear it natively and fire sl-input,
    // scheduling a result load after the dialog has gone. Cancelled before the no-rows return so an
    // empty result list is covered too.
    if (e.key === 'Escape') e.preventDefault();
    if (this._rows.length === 0) return;
    const last = this._rows.length - 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._highlightedIndex =
          this._highlightedIndex < 0
            ? 0
            : Math.min(this._highlightedIndex + 1, last);
        this._scrollHighlightedIntoView();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._highlightedIndex =
          this._highlightedIndex < 0
            ? last
            : Math.max(this._highlightedIndex - 1, 0);
        this._scrollHighlightedIntoView();
        break;
      case 'Home':
        e.preventDefault();
        this._highlightedIndex = 0;
        this._scrollHighlightedIntoView();
        break;
      case 'End':
        e.preventDefault();
        this._highlightedIndex = last;
        this._scrollHighlightedIntoView();
        break;
      case 'Enter':
        if (this._highlightedIndex >= 0 && this._highlightedIndex <= last) {
          e.preventDefault();
          this._onRowClick(this._rows[this._highlightedIndex]);
        }
        break;
    }
  }

  private _rowId(i: number): string {
    return `${this._instanceId}-row-${i}`;
  }

  private _scrollHighlightedIntoView() {
    void this.updateComplete.then(() => {
      const row = this.shadowRoot?.getElementById(
        this._rowId(this._highlightedIndex)
      );
      row?.scrollIntoView({ block: 'nearest' });
    });
  }

  private async _onLoadMore() {
    await this._loadRows(true);
  }

  render() {
    const dialogLabel = this.label ? `Select ${this.label}` : 'Select';
    const dialogId = `${this._instanceId}-dialog`;
    return html`
      <sl-dialog
        id=${dialogId}
        label=${dialogLabel}
        ?open=${this.open}
        @sl-request-close=${this._requestClose}
      >
        ${
          this._metaLoading
            ? html`<div class="loading-row"><sl-spinner></sl-spinner></div>`
            : this._metaError
              ? html`<sl-alert variant="danger" open
                  >${this._metaError}</sl-alert
                >`
              : this._renderSearchBody()
        }
        <sl-button slot="footer" variant="default" @click=${this._requestClose}>
          Cancel
        </sl-button>
      </sl-dialog>
    `;
  }

  private _renderSearchBody() {
    if (!this._meta) return nothing;
    const resultsId = `${this._instanceId}-results`;
    const activeRowId =
      this._highlightedIndex >= 0
        ? this._rowId(this._highlightedIndex)
        : undefined;
    return html`
      <div class="results-shell">
        <!-- type="search": a search box is not a credential field, which is what keeps Safari and password managers from offering saved logins here (FORM-AUTOFILL; data-entry-autofill.md). -->
        <sl-input
          type="search"
          class="dialog-search"
          placeholder=${this._meta.freeTextHint ?? 'Search…'}
          clearable
          autofocus
          .value=${live(this._freeText)}
          role="combobox"
          aria-expanded="true"
          aria-controls=${resultsId}
          aria-autocomplete="list"
          aria-activedescendant=${ifDefined(activeRowId)}
          @sl-input=${this._onFreeTextInput}
          @sl-clear=${() => {
            this._freeText = '';
            void this._loadRows(false);
          }}
          @keydown=${this._onDialogSearchKeyDown}
        >
          <sl-icon name="search" slot="prefix"></sl-icon>
        </sl-input>

        ${
          this._error
            ? html`<sl-alert variant="warning" open>${this._error}</sl-alert>`
            : nothing
        }

        <div
          class="results-table-wrapper ${
            this._loading && this._rows.length > 0 ? 'is-refreshing' : ''
          }"
        >
          ${
            this._loading && this._rows.length === 0
              ? html`<div class="loading-row"><sl-spinner></sl-spinner></div>`
              : this._rows.length === 0
                ? html`<div class="empty-state">No results found.</div>`
                : html`
                    ${this._renderTable()}
                    ${
                      this._loading
                        ? html`
                            <div
                              class="results-refresh-veil"
                              aria-hidden="true"
                            >
                              <sl-spinner></sl-spinner>
                            </div>
                          `
                        : nothing
                    }
                  `
          }
        </div>

        ${
          this._hasMore
            ? html`
                <div class="load-more-row">
                  <sl-button
                    size="small"
                    variant="default"
                    ?loading=${this._loading}
                    @click=${this._onLoadMore}
                  >
                    Load more
                  </sl-button>
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  private _renderTable() {
    const columns = this._meta!.columns;
    return html`
      <table
        class="results-table"
        role="grid"
        id="${this._instanceId}-results"
        aria-rowcount=${this._rows.length}
      >
        <thead>
          <tr role="row">
            ${columns.map(
              (col) => html`
                <th
                  role="columnheader"
                  class="align-${col.align.toLowerCase()} priority-${(
                    col.priority ?? 'Primary'
                  ).toLowerCase()}"
                >
                  ${col.label}
                </th>
              `
            )}
          </tr>
        </thead>
        <tbody>
          ${this._rows.map(
            (row, i) => html`
              <tr
                role="row"
                id=${this._rowId(i)}
                aria-selected=${i === this._highlightedIndex ? 'true' : 'false'}
                class="data-row ${
                  i === this._highlightedIndex ? 'is-highlighted' : ''
                }"
                @click=${() => this._onRowClick(row)}
              >
                ${columns.map((col) => {
                  const val = row.columnValues?.[col.key] ?? '';
                  const classes = [
                    `align-${col.align.toLowerCase()}`,
                    `priority-${(col.priority ?? 'Primary').toLowerCase()}`,
                    col.renderer ? `renderer-${col.renderer}` : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return html`
                    <td
                      role="gridcell"
                      class=${classes}
                      title=${typeof val === 'string' ? val : ''}
                    >
                      ${this._renderCell(val, col.renderer)}
                    </td>
                  `;
                })}
              </tr>
            `
          )}
        </tbody>
      </table>
    `;
  }

  private _renderCell(value: string, renderer: string | undefined) {
    if (renderer === 'status') {
      // Label resolution, not a domain: search rows carry whatever status word their search
      // handler sent. A word the registry does not know renders the unmapped look - the old
      // _statusVariant here sent every unknown value to amber, which is the defect D-040 records.
      return html`<agwa-state-badge state=${value}></agwa-state-badge>`;
    }
    return value || '—';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-search-dialog': AgwaSearchDialog;
  }
}
